import { ArrowRightIcon } from '@phosphor-icons/react'
import { type DimensionScore } from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'
import * as styles from './repo-risk-scorecard.css.ts'
import * as bandStyles from './scorecard-bands.css.ts'
import { scoreTagLabel, toScoreBand } from './scorecard-bands.ts'

export type RepoRiskScorecardProps = {
  dimension: DimensionScore
  onSelect: (dimensionId: string) => unknown
}

/**
 * One dimension of a repository's security posture, as a card in the scorecards grid.
 *
 * The whole card is the control rather than the "View" affordance alone: the affordance names the action,
 * but a 320px card whose only target is a 40px label reads as a card that happens to contain a link.
 */
export const RepoRiskScorecard: React.FC<RepoRiskScorecardProps> = ({ dimension, onSelect }) => {
  const band = toScoreBand(dimension.score)
  const confidencePercent = Math.round(dimension.confidence * 100)

  return (
    <button
      className={styles.card}
      onClick={() => onSelect(dimension.dimension)}
      aria-label={`View dimension: ${dimension.displayName}`}
    >
      <div className={styles.cardHeader}>
        <p className={styles.cardTitle}>{dimension.displayName}</p>
        <span className={bandStyles.scoreTag({ band })}>{scoreTagLabel(dimension.score)}</span>
      </div>
      {/* Full width rather than beside the tag: what the dimension measures is the reader's way into the
          card, and a column two thirds the card's width made it read as a caption on the title. */}
      <p className={styles.cardSubtitle}>{dimension.description}</p>
      <p className={styles.cardRationale}>{dimension.rationale}</p>
      <div className={styles.cardFooter}>
        <p className={styles.cardMeta}>
          {confidencePercent}% confidence · {dimension.answers.length}{' '}
          {pluralize(dimension.answers.length, 'question', 'questions')}
        </p>
        <span className={styles.viewAffordance}>
          View
          <ArrowRightIcon size={16} weight="light" />
        </span>
      </div>
    </button>
  )
}
